import {
  useMutation,
  useQuery,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AddPortalTimeEntryInput,
  AddPortalTimeEntryResult,
  ProjectPortalAssignmentsView,
  ProjectPortalCloseResult,
  ProjectPortalDatesResult,
  ProjectPortalTaskAssignment,
  ProjectPortalView,
  ProjectPortalWeekStatus,
  UpdatePortalEndDateInput,
  UpdatePortalTaskAssigneeInput,
} from '@catalyst-psa/shared';

import { publicApi } from './publicApi.js';

export function useProjectPortalView(token: string): UseQueryResult<ProjectPortalView> {
  return useQuery({
    queryKey: ['public-portal', token],
    enabled: Boolean(token),
    retry: false,
    queryFn: () => publicApi.get<ProjectPortalView>(`/public/projects/${token}`),
  });
}

export function useProjectPortalWeekStatus(
  token: string,
  weekStart: string,
  enabled: boolean,
): UseQueryResult<ProjectPortalWeekStatus> {
  return useQuery({
    queryKey: ['public-portal', token, 'week-status', weekStart],
    enabled: enabled && Boolean(token) && Boolean(weekStart),
    retry: false,
    queryFn: () =>
      publicApi.get<ProjectPortalWeekStatus>(
        `/public/projects/${token}/week-status?weekStart=${weekStart}`,
      ),
  });
}

export function useAddPortalTimeEntry(
  token: string,
): UseMutationResult<AddPortalTimeEntryResult, unknown, AddPortalTimeEntryInput> {
  return useMutation({
    mutationFn: (input: AddPortalTimeEntryInput) =>
      publicApi.post<AddPortalTimeEntryResult>(`/public/projects/${token}/time-entries`, input),
  });
}

export function useClosePortalProject(
  token: string,
): UseMutationResult<ProjectPortalCloseResult, unknown, void> {
  return useMutation({
    mutationFn: () => publicApi.post<ProjectPortalCloseResult>(`/public/projects/${token}/close`),
  });
}

/** Updates the project's end date from the portal's "For Catalyst use only" section. */
export function useUpdatePortalEndDate(
  token: string,
): UseMutationResult<ProjectPortalDatesResult, unknown, UpdatePortalEndDateInput> {
  return useMutation({
    mutationFn: (input: UpdatePortalEndDateInput) =>
      publicApi.post<ProjectPortalDatesResult>(`/public/projects/${token}/end-date`, input),
  });
}

export function useProjectPortalAssignments(
  token: string,
  enabled: boolean,
): UseQueryResult<ProjectPortalAssignmentsView> {
  return useQuery({
    queryKey: ['public-portal', token, 'assignments'],
    enabled: enabled && Boolean(token),
    retry: false,
    queryFn: () =>
      publicApi.get<ProjectPortalAssignmentsView>(`/public/projects/${token}/assignments`),
  });
}

export function useUpdatePortalTaskAssignee(
  token: string,
): UseMutationResult<
  ProjectPortalTaskAssignment,
  unknown,
  UpdatePortalTaskAssigneeInput & { ticketId: string }
> {
  return useMutation({
    mutationFn: ({ ticketId, ...input }: UpdatePortalTaskAssigneeInput & { ticketId: string }) =>
      publicApi.post<ProjectPortalTaskAssignment>(
        `/public/projects/${token}/tasks/${ticketId}/assignees`,
        input,
      ),
  });
}
